/**
 * Helpers for the Readiness > Employees filter bar (ER-10506 / MDP-552, Rules page
 * "Readiness > Positions View" Rule 5).
 *
 * The legacy filter bar only had a Recalculation radio group, which Rule 5 removes.
 * It is replaced by:
 *
 *   - Excluded (radio):        exclude_from_avg, values EXCLUDED / NOT_EXCLUDED on the
 *                              Employees v2 list API; human labels "Excluded" /
 *                              "Not Excluded"; default "No Filter" (unset).
 *   - Department (picker):     department id + department_scope ("direct" | "subtree"),
 *                              matching the employee's current primary position's
 *                              department (same params already used by MDP-440 on the
 *                              Organization > Employees list).
 *
 * Values are stable lowercase snake_case end-to-end ("excluded", "not_excluded"); the
 * uppercase EXCLUDED / NOT_EXCLUDED enum is only produced when the filter is translated
 * to an API query parameter. The helpers also accept the human label strings so
 * persisted values survive whatever path produced them (same convention as the
 * FilterEmployeeModal radio payload helpers).
 */

export const EXCLUDE_FROM_AVG_FILTER_GROUP = Object.freeze({
  key: "excluded",
  label: "Excluded",
  items: [
    { label: "No Filter", value: "" },
    { label: "Excluded", value: "excluded" },
    { label: "Not Excluded", value: "not_excluded" },
  ],
});

/** value slug -> display label. */
export const EXCLUDE_FROM_AVG_LABEL_BY_VALUE = Object.freeze({
  excluded: "Excluded",
  not_excluded: "Not Excluded",
});

/** display label -> value slug. */
const EXCLUDE_FROM_AVG_VALUE_BY_LABEL = Object.freeze(
  Object.entries(EXCLUDE_FROM_AVG_LABEL_BY_VALUE).reduce((acc, [value, label]) => {
    acc[label] = value;
    return acc;
  }, {})
);

/** Stable slug -> API enum (ER-10505 backend contract). */
export const EXCLUDE_FROM_AVG_API_VALUE = Object.freeze({
  excluded: "EXCLUDED",
  not_excluded: "NOT_EXCLUDED",
});

/**
 * Normalize one applied-filter token to the stable exclude_from_avg slug.
 * Accepts the lowercase slug and the human label; anything else yields "".
 */
export function normalizeExcludeFromAvgValue(value) {
  const token = String(value || "").trim();
  if (!token) return "";
  if (EXCLUDE_FROM_AVG_LABEL_BY_VALUE[token]) return token;
  return EXCLUDE_FROM_AVG_VALUE_BY_LABEL[token] || "";
}

/** Applied-filter array -> { excluded } slug ("" when the radio is unset). */
export function buildExcludeFromAvgPayload(filters = []) {
  const values = Array.isArray(filters) ? filters : [];
  const payload = { excluded: "" };

  const item = EXCLUDE_FROM_AVG_FILTER_GROUP.items.find(
    (option) =>
      option.value && values.some((value) => normalizeExcludeFromAvgValue(value) === option.value)
  );
  if (item) payload.excluded = item.value;

  return payload;
}

/** Payload -> applied-filter array (only non-default values). */
export function excludeFromAvgToValues(payload = {}) {
  const value = normalizeExcludeFromAvgValue(payload?.excluded);
  return value ? [value] : [];
}

/**
 * Applied-filter array (slugs or labels) -> Employees v2 list API query params.
 * Returns {} when the Excluded radio is unset.
 */
export function excludeFromAvgToApi(filters = []) {
  const slug = normalizeExcludeFromAvgValue(
    (Array.isArray(filters) ? filters : []).find((value) => !!normalizeExcludeFromAvgValue(value))
  );
  if (!slug) return {};
  return { exclude_from_avg: EXCLUDE_FROM_AVG_API_VALUE[slug] };
}

/** Stable slug -> display label ("" when unknown). */
export function getExcludeFromAvgLabel(value) {
  const slug = normalizeExcludeFromAvgValue(value);
  return EXCLUDE_FROM_AVG_LABEL_BY_VALUE[slug] || "";
}

/** Display label -> stable slug ("" when unknown). */
export function getExcludeFromAvgValueFromLabel(label) {
  return normalizeExcludeFromAvgValue(label);
}

/** Legacy Recalculation labels (kept for chip display of persisted pre-MDP-552 values). */
export const RECALCULATION_LABEL_BY_VALUE = Object.freeze({
  no_filter: "No Filter",
  requires_recalc: "Requires Recalc",
});

/**
 * Display label for a persisted Readiness > Employees applied-filter value.
 * Covers the new Excluded group, the legacy Recalculation group and unknown
 * tokens (returned as-is, matching SearchBar's raw-value chip fallback).
 */
export function getReadinessEmployeeFilterLabel(value) {
  const excludeLabel = getExcludeFromAvgLabel(value);
  if (excludeLabel) return excludeLabel;
  if (RECALCULATION_LABEL_BY_VALUE[value]) return RECALCULATION_LABEL_BY_VALUE[value];
  return value;
}

/** Display label -> persisted slug (label passthrough when unknown). */
export function getReadinessEmployeeFilterValueFromLabel(labelOrValue) {
  if (!labelOrValue) return labelOrValue;
  const excludeSlug = getExcludeFromAvgValueFromLabel(labelOrValue);
  if (excludeSlug) return excludeSlug;
  const recalcEntry = Object.entries(RECALCULATION_LABEL_BY_VALUE).find(
    ([, label]) => label === labelOrValue
  );
  if (recalcEntry) return recalcEntry[0];
  return labelOrValue;
}
